import { useState, useEffect, useRef } from 'react';
import { Button } from 'src/@/components/ui/button';
import { ScrollArea, ScrollBar } from 'src/@/components/ui/scroll-area';
import { Textarea } from 'src/@/components/ui/textarea';
import { Card, CardContent } from "src/@/components/ui/card";
import v2_actionAgentsPredictions from 'src/actions/v2/ai/v2_actionAgentsPredictions';
import { Loader2, Trash2, Send, MessageSquare } from "lucide-react";

export default function AiAgentPlaygroundTab({ agentId, aiAgent }) {
    const [userInput, setUserInput] = useState('');
    const [conversation, setConversation] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const scrollAreaRef = useRef(null);

    // Carregar conversas do localStorage
    useEffect(() => {
        const savedConversation = localStorage.getItem(`chat_history_${agentId}`);
        if (savedConversation) {
            setConversation(JSON.parse(savedConversation));
        } else if (aiAgent?.config?.promptGuide?.prompt) {
            const initialHistory = [];
            setConversation(initialHistory);
        }
    }, [agentId, aiAgent]);

    // Salvar conversas no localStorage quando houver mudanças
    useEffect(() => {
        if (conversation.length > 0) {
            localStorage.setItem(`chat_history_${agentId}`, JSON.stringify(conversation));
        }
    }, [conversation, agentId]);

    useEffect(() => {
        if (scrollAreaRef.current) {
            const scrollArea = scrollAreaRef.current;
            scrollArea.scrollTop = scrollArea.scrollHeight;
        }
    }, [conversation]);

    const simulateTyping = async (content) => {
        setIsTyping(true);
        await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
        setIsTyping(false);
        return content;
    };

    const handleClearChat = () => {
        const initialHistory = [];
        setConversation(initialHistory);
        localStorage.setItem(`chat_history_${agentId}`, JSON.stringify(initialHistory));
    };

    const handleSubmit = async () => {
        if (!userInput.trim() || isLoading) return;

        try {
            setIsLoading(true);
            const newMessage = { role: 'user', content: userInput };
            const updatedConversation = [...conversation, newMessage];
            setConversation(updatedConversation);
            setUserInput('');

            const data = {
                scope: {
                    history: updatedConversation
                }
            };

            const response = await v2_actionAgentsPredictions(agentId, data);
            const assistantMessage = response.data.choices[0].message.content;
            
            const processedMessage = await simulateTyping(assistantMessage);
            
            const aiResponse = { 
                role: 'assistant', 
                content: processedMessage
            };
            
            const finalConversation = [...updatedConversation, aiResponse];
            setConversation(finalConversation);
            localStorage.setItem(`chat_history_${agentId}`, JSON.stringify(finalConversation));
        } catch (error) {
            console.error('Erro ao enviar mensagem:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card className="flex flex-col h-[calc(100vh-300px)]">
            {/* Header */}
            <div className="flex items-center justify-between p-2 border-b bg-gradient-to-b from-slate-50 to-slate-100">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium">Playground</h3>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearChat}
                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                    <Trash2 className="w-4 h-4" />
                    Limpar Chat
                </Button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                    <div className="flex flex-col gap-4 p-4">
                        {conversation.map((msg, index) => (
                            <div
                                key={index}
                                className={`flex ${
                                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                                }`}
                            >
                                <div
                                    className={`
                                        relative p-4 rounded-xl max-w-[80%]
                                        ${msg.role === 'user' 
                                            ? 'bg-violet-600 text-primary-foreground rounded-tr-none' 
                                            : msg.role === 'system'
                                            ? 'bg-secondary/50 rounded-tl-none'
                                            : 'bg-muted rounded-tl-none'
                                        }
                                        shadow-sm hover:shadow-md transition-shadow
                                    `}
                                >
                             
                                    <p className="text-xs leading-relaxed whitespace-pre-wrap">
                                        {msg.content}
                                    </p>
                 
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-muted p-4 rounded-md rounded-tl-none shadow-sm">
                                    <div className="flex gap-2 items-center">
                                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
                                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-.3s]" />
                                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-.5s]" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <ScrollBar />
                </ScrollArea>
            </div>

            {/* Input Area */}
            <div className="border-t p-4 bg-background">
                <div className="flex gap-2">
                    <Textarea
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        placeholder="Digite sua mensagem..."
                        className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                    />
                    <Button 
                        onClick={handleSubmit}
                        disabled={isLoading || !userInput.trim()}
                        className="h-auto px-6"
                    >
                        {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <div className="flex items-center gap-2">
                                <Send className="w-4 h-4" />
                                Enviar
                            </div>
                        )}
                    </Button>
                </div>
            </div>
        </Card>
    );
} 